import { createDictionaryFromList, flattenArrayByKey } from '@protector/utils/array';
import { getStringOrLocalizedString } from '@protector/utils/i18n';
import { formatArea } from '@protector/utils/l10n';
import { hashNumber } from '@protector/utils/number';
import { getStringOrDefault } from '@protector/utils/string';
import type { Dictionary, UUID } from '@protector/utils/types';
import { COLOR_OPTIONS } from '@protector/styles';
import type { Feature, FeatureCollection, Geometry } from 'geojson';
import { groupBy, isEmpty, uniqBy } from 'lodash';
import moment from 'moment';
import type { Update } from 'redux-ngrx-entity';
import { MAP_DAE_COLORS_5, MAP_SEVERITY_COLORS_5, MAP_SPRAY_COLORS_5 } from '../choropleth/constants.ts';
import type { ChoroplethLimits, StorageMaxDaysLimit } from '../choropleth/models.ts';
import {
  getChoroplethSeverityColor,
  getLimitsChoropleth,
  getPhenologicalStageColor,
  getSeedsChoropleth,
  getSortedPhenologicalStageColor,
  getSortedSeedsChoropleth,
  getStorageDaysLimit
} from '../choropleth/utils.ts';
import * as PropertyActions from '../property/__legacy__/redux.actions.ts';
import type { CurrentSeasonArea, IndicatorPressureResponse, Property, PropertyInfoAddress } from '../property/models.ts';
import type { Season } from '../season/models.ts';
import { isActive } from '../season/utils.ts';
import type { TimelineWindowEvents } from '../timeline/models.ts';
import { getColorGeneralWithMaxValue, getSeverityColorFromSeverity } from '../timeline/utils.ts';
import { RegionType, SeasonAreaUpdateType } from './constants.ts';
import type {
  ColorDictionary,
  CurrentInfo,
  GeometryProperties,
  Region,
  RegionGeometry,
  RegionWithParents,
  UpdateFeaturePropertiesParams,
  UpdateSeasonAreasMassivelyPayload
} from './models.ts';

const CROP_ENDED_PHENOLOGY_COLOR = COLOR_OPTIONS.GRAY_4;

export const isFeatureCollection = (geometry?: RegionGeometry): geometry is FeatureCollection<Geometry, GeometryProperties> => {
  if (!geometry) return false;
  return 'features' in geometry;
};

export const getUpdatedChildRegionsWithIndicatorPressure = (
  regions: Dictionary<Region>,
  indicatorPressureResultMap: Dictionary<IndicatorPressureResponse>,
  useNewMapColors = false
): Region[] => {
  return Object.values(regions)
    .filter(region => !isFeatureCollection(region.geometry))
    .map(oldRegion => {
      const indicatorResult = indicatorPressureResultMap[oldRegion.id];
      const index = !indicatorResult?.indexes.length ? undefined : indicatorResult.indexes[0];
      const oldGeometry: GeoJSON.Feature = oldRegion.geometry as GeoJSON.Feature;

      let indicatorPressureColor;
      let indicator_pressure: string | undefined = undefined;
      if (index?.severity) {
        indicatorPressureColor = getSeverityColorFromSeverity(index.severity, useNewMapColors);
      }
      if (typeof index?.result === 'number') {
        indicator_pressure = `${index?.result?.toFixed(index?.indicator?.decimal_places)} ${getStringOrLocalizedString(
          index.indicator.uom
        )}`;
      }

      const newRegion: Region = oldRegion;

      if (newRegion?.geometry) {
        newRegion.geometry = {
          ...oldGeometry,
          properties: {
            ...(oldGeometry.properties as GeometryProperties),
            indicatorPressureColor,
            indicator_pressure
          }
        };
      } else {
        newRegion.geometry = undefined;
      }

      return newRegion;
    });
};

const getAreaRegions = (regions: Dictionary<Region>): Region[] =>
  Object.values(regions).filter(region => !region.children || region.children.length === 0);

export const getAreaIdsFromRegions = (regions: Dictionary<Region>): UUID[] => getAreaRegions(regions).map(region => region.id);

const getNonAreaRegions = (regions: Dictionary<Region>): Region[] => {
  const areaRegionIds = getAreaIdsFromRegions(regions);
  return Object.values(regions).filter(region => !areaRegionIds.includes(region.id));
};

const updateFeatureProperties = ({ feature, regionUpdatesIds, regionGeometryPropertiesMap }: UpdateFeaturePropertiesParams) => {
  const idArea = (feature.properties as unknown as GeoJSON.Feature).id as string;
  if (feature.properties && regionUpdatesIds.has(idArea)) {
    feature.properties = {
      ...feature.properties,
      ...regionGeometryPropertiesMap[idArea]
    };
  }
  return feature;
};

export const getUpdatedParentRegionsWithIndicatorPressure = (regions: Dictionary<Region>, updatedChildRegions: Region[]): Region[] => {
  const regionUpdatesIds = new Set(updatedChildRegions.map(update => update.id));

  const regionGeometryPropertiesMap = createDictionaryFromList(
    updatedChildRegions,
    'id',
    region => (region.geometry as GeoJSON.Feature)?.properties
  );

  return getNonAreaRegions(regions).map((region): Region => {
    const geometries = isFeatureCollection(region.geometry)
      ? {
          ...region.geometry,
          features: region.geometry.features.map(feature =>
            updateFeatureProperties({ feature, regionUpdatesIds, regionGeometryPropertiesMap })
          )
        }
      : region.geometry;
    return {
      ...region,
      geometry: geometries as unknown as RegionGeometry
    };
  });
};

export const hasOtherEvents = (events: TimelineWindowEvents) => {
  return (
    events.annotation_window ??
    events.monitoring_window ??
    events.phenology_window ??
    events.spray_window ??
    events.harvest_window ??
    events.irrigation_window ??
    events.planting_window ??
    events.emergence_window ??
    events.flowering_window ??
    events.detasseling_window ??
    events.roguing_window ??
    events.male_destruction_window ??
    events.flaming_window
  );
};

export const mapRegionToRegionUpdate = (region: Region): Update<Region> => ({ id: region.id, changes: region });

export function selectRegionUuid(region: Region): string {
  return region.id;
}

export const setSeasonMethodologies = (
  methodologies: Dictionary<{
    seasonId?: UUID;
    methodologyId: UUID;
  }>,
  regions: Dictionary<Region>
) => {
  return Object.keys(methodologies).map(regionId => {
    if (!regions[regionId]) {
      return {
        id: regionId,
        changes: {}
      };
    }
    const parsedSeasons = regions[regionId].seasons.map(season => {
      if (season.id !== methodologies[regionId].seasonId) return season;

      return {
        ...season,
        methodologyIds: [methodologies[regionId].methodologyId]
      };
    });

    return {
      id: regionId,
      changes: {
        seasons: parsedSeasons
      }
    };
  });
};

export function sortByName(a: Region, b: Region): number {
  return a.name.localeCompare(b.name);
}

export const verifyPressureIndicator = (action: ReturnType<typeof PropertyActions.LoadSeverityByIndicatorSuccess>): boolean | undefined => {
  return action.payload.data?.some(payloadData =>
    !isEmpty(payloadData.indexes?.[0].severity) ? payloadData.indexes[0].severity : undefined
  );
};

const isFieldFeatureVisible = (
  feature: Feature<Geometry, GeometryProperties>,
  showFieldsCropEnded: boolean,
  showFieldsCropFuture: boolean,
  showArchivedFields: boolean,
  cropStatusIsKnown: boolean
): boolean => {
  if (feature.properties?.is_archived && !showArchivedFields) return false;
  const currentInfo = feature.properties?.current_info;
  if (!currentInfo) return cropStatusIsKnown || showFieldsCropEnded;
  if (currentInfo.crop_ended) return showFieldsCropEnded;
  if (currentInfo.crop_future) return showFieldsCropFuture;
  return true;
};

export const getRootRegion = (
  regions: Region[],
  properties: Dictionary<Property>,
  selectedProperty: string,
  showFieldsCropEnded = true,
  showFieldsCropFuture = true,
  showArchivedFields = true,
  cropStatusIsKnown = true
) => {
  let rootRegion = regions.find(region => region.id === properties[selectedProperty].root_region_id) ?? null;

  if (!rootRegion) {
    const regionsWithChildren = regions.filter(region => !!region.children?.length);
    if (regionsWithChildren.length === 1) {
      [rootRegion] = regionsWithChildren;
    }
  }

  if (
    rootRegion &&
    (!showFieldsCropEnded || !showFieldsCropFuture || !showArchivedFields) &&
    rootRegion?.geometry?.type === 'FeatureCollection'
  ) {
    const {
      geometry: { features = [], type }
    } = rootRegion;

    const { filteredFeatures, children } = features.reduce(
      (acc, feature) => {
        if (isFieldFeatureVisible(feature, showFieldsCropEnded, showFieldsCropFuture, showArchivedFields, cropStatusIsKnown)) {
          acc.filteredFeatures.push(feature);
          const featureId = feature.properties?.id;
          if (typeof featureId === 'string') {
            acc.children.push(featureId);
          }
        }
        return acc;
      },
      { filteredFeatures: [] as Feature<Geometry, GeometryProperties>[], children: [] as string[] }
    );

    rootRegion = {
      ...rootRegion,
      children,
      geometry: {
        features: filteredFeatures,
        type
      }
    };
  }

  return rootRegion;
};

export const getRegionChildrenMapping = (regions: Region[]): Record<string, Region[]> => {
  const regionChildrenMapping: Record<string, Region[]> = {};
  regions.forEach(region => {
    if (region?.parent_id) {
      if (regionChildrenMapping?.[region.parent_id]) {
        regionChildrenMapping[region.parent_id].push(region);
      } else {
        regionChildrenMapping[region.parent_id] = [region];
      }
    }
  });

  return regionChildrenMapping;
};

export const getDateEditPayloadFromEditField = (
  field: SeasonAreaUpdateType,
  formattedDate: string,
  seasonAreas: UUID[]
): UpdateSeasonAreasMassivelyPayload => {
  return {
    model: {
      varieties: field === SeasonAreaUpdateType.VARIETIES ? [] : undefined,
      emergencyDate: field === SeasonAreaUpdateType.EMERGENCY_DATE ? formattedDate : undefined,
      harvestingDate: field === SeasonAreaUpdateType.HARVESTING_DATE ? formattedDate : undefined,
      plantingDate: field === SeasonAreaUpdateType.PLANTING_DATE ? formattedDate : undefined
    },
    seasonIds: seasonAreas,
    fieldToUpdate: field
  };
};
export const normalizeRegion = (region: Region): Dictionary<Region> => {
  const result: Dictionary<Region> = {};

  const changeChildrenToIdsArray = (innerRegion: Region) => {
    const children =
      innerRegion.children?.map(child => {
        if (typeof child === 'string') return child;

        changeChildrenToIdsArray(child);

        return child.id;
      }) ?? null;

    result[innerRegion.id] = {
      ...innerRegion,
      children
    };
  };

  changeChildrenToIdsArray(region);

  return result;
};

/**
 * Rewrites each field's seasons from the season-areas pairs, rolling the union up through the
 * parent regions. The region tree carries a single season per field — the one whose cropzone gave
 * the geometry — so a caller that reads the tree for another season has to restore the membership
 * from the pairs the API answers with.
 * @param regions Normalized region dictionary to copy from.
 * @param seasonAreas Field ↔ season pairs for the seasons the caller asked for.
 * @param seasons Season entities the pairs resolve to; a pair whose season is absent is ignored.
 */
export const enrichRegionsWithAllMatchingSeasons = (
  regions: Dictionary<Region>,
  seasonAreas: CurrentSeasonArea[],
  seasons: Season[]
): Dictionary<Region> => {
  const seasonsById = new Map(seasons.map(season => [season.id, season]));
  const seasonsByAreaId = new Map<string, Map<string, Season>>();

  seasonAreas.forEach(seasonArea => {
    const season = seasonsById.get(seasonArea.seasonId);
    if (!season) return;
    const accumulator = seasonsByAreaId.get(seasonArea.areaId) ?? new Map<string, Season>();
    accumulator.set(season.id, season);
    seasonsByAreaId.set(seasonArea.areaId, accumulator);
  });

  const enriched: Dictionary<Region> = { ...regions };
  const visited = new Set<UUID>();

  const enrichRegionDepthFirst = (regionId: UUID): Season[] => {
    if (visited.has(regionId)) return enriched[regionId]?.seasons ?? [];
    visited.add(regionId);

    const region = enriched[regionId];
    if (!region) return [];

    if (region.type === RegionType.AREA) {
      const matchedSeasons = seasonsByAreaId.get(regionId);
      if (!matchedSeasons?.size) return region.seasons ?? [];

      const areaSeasons = [...matchedSeasons.values()];
      enriched[regionId] = { ...region, seasons: areaSeasons };
      return areaSeasons;
    }

    const childrenIds = (region.children as UUID[] | undefined) ?? [];
    const rollup = new Map<string, Season>();
    childrenIds.forEach(childId => {
      enrichRegionDepthFirst(childId).forEach(season => {
        if (!rollup.has(season.id)) rollup.set(season.id, season);
      });
    });

    const rolledSeasons = [...rollup.values()];
    enriched[regionId] = { ...region, seasons: rolledSeasons };
    return rolledSeasons;
  };

  Object.keys(regions).forEach(regionId => enrichRegionDepthFirst(regionId));

  return enriched;
};

export const rootRegionWithChildren = (regions: Dictionary<Region>, rootRegionId: UUID): Region => {
  const changeChildrenToRegionsArray = (regionId: UUID): Region | undefined => {
    const innerRegion = regions[regionId];

    if (!innerRegion?.children) return innerRegion;

    const children = innerRegion.children.reduce<Region[]>((acc, child) => {
      if (typeof child !== 'string') {
        acc.push(child);
        return acc;
      }

      const resolvedChild = changeChildrenToRegionsArray(child);
      if (resolvedChild) acc.push(resolvedChild);

      return acc;
    }, []);

    return {
      ...innerRegion,
      children
    };
  };

  return changeChildrenToRegionsArray(rootRegionId) as Region;
};

const mergeCropFuture = (fresh: CurrentInfo, stored?: CurrentInfo): boolean | undefined => {
  if (fresh.crop_ended) return false;
  return fresh.crop_future ?? stored?.crop_future;
};

export const toDeepRegion = (normalizedRegion: Region, currentInfoMap: Dictionary<CurrentInfo>, regions: Dictionary<Region>) => {
  if (
    normalizedRegion.children?.every(
      child =>
        typeof child !== 'string' &&
        (child.current_info === currentInfoMap[child.id] || child.current_info === regions[child.id].current_info)
    )
  ) {
    return normalizedRegion;
  }

  return {
    ...normalizedRegion,
    children: (normalizedRegion.children as string[]).map(fieldId => ({
      ...regions[fieldId],
      current_info: currentInfoMap[fieldId]
        ? { ...currentInfoMap[fieldId], crop_future: mergeCropFuture(currentInfoMap[fieldId], regions[fieldId]?.current_info) }
        : regions[fieldId]?.current_info
    }))
  };
};

const getRegionsChild = (region: Region): Region[] => {
  return flattenArrayByKey(region.children as Region[], 'children').filter((x: Region) => x.type === RegionType.REGION);
};

const mapAreaChildren = (children: Region[], dictSeasonAreas: Record<string, CurrentSeasonArea[]>, selectedSeasons: string[]) => {
  const childrens = children.reduce((accumulator, child) => {
    if (child.type !== RegionType.AREA) {
      accumulator.push(child);
    } else {
      if (child.current_info && !child.current_info.crop_future) {
        child.current_info.crop_ended = dictSeasonAreas
          ? !dictSeasonAreas[child.id]?.some(as => selectedSeasons?.includes(as.seasonId))
          : false;
      }
      accumulator.push(child);
    }
    return accumulator;
  }, [] as Region[]);

  return uniqBy(childrens, 'id');
};

export const getRegionAreasWithParents = (region: Region, parents = [] as UUID[]): RegionWithParents[] => {
  return (region.children as Region[]).flatMap(child => {
    if (child.type === RegionType.REGION) return getRegionAreasWithParents(child, [...parents, region.id]);

    return { ...child, parents } as RegionWithParents;
  });
};

export const getDAEByEmergenceDay = (emergenceDay: string | undefined) => {
  if (emergenceDay) {
    return moment().startOf('day').diff(moment(emergenceDay).startOf('day'), 'days');
  }
  return undefined;
};

export const validateSprayDateTime = (current_info?: CurrentInfo, crop_ended?: boolean): boolean => {
  if (!current_info || crop_ended) return false;

  const sprayDate = current_info?.last_spray?.start_date?.slice(0, 10);
  if (!sprayDate) return false;

  const monitoringDate = current_info?.last_monitoring?.date?.slice(0, 10);
  if (!monitoringDate) return true;

  return sprayDate >= monitoringDate;
};

export const isInReentryPeriod = (current_info?: CurrentInfo, crop_ended?: boolean): boolean => {
  if (!current_info || crop_ended) return false;

  const lastSpray = current_info.last_spray;
  if (!lastSpray) return false;

  const now = moment();
  const startDate = moment(lastSpray.start_date);
  if (startDate.isValid() && now.isBefore(startDate)) return false;

  const endDate = moment(lastSpray.end_date || lastSpray.start_date);
  const reentryPeriod = lastSpray.details?.reentry_period;

  if (endDate.isValid() && reentryPeriod && reentryPeriod > 0) {
    const restrictedEntryUom = lastSpray.details?.restricted_entry_uom?.toLowerCase();
    const reentryUnit: moment.unitOfTime.DurationConstructor = restrictedEntryUom?.startsWith('hour') ? 'hours' : 'days';
    const reentryEndDate = endDate.clone().add(reentryPeriod, reentryUnit);
    return !now.isAfter(reentryEndDate);
  }

  if (lastSpray.details?.reentry_after) {
    const reentryAfterDate = moment(lastSpray.details.reentry_after);
    if (!reentryAfterDate.isValid()) return false;
    return !now.isAfter(reentryAfterDate);
  }

  if (startDate.isValid() && endDate.isValid() && now.isSameOrBefore(endDate)) {
    return true;
  }

  return false;
};

interface OptionsMultilayer {
  showMapSeedsLayer?: boolean;
  showMapPhenologicalStageLayer?: boolean;
  showMapDAE?: boolean;
}

export const getDaysAfterEmergenceColor = (
  area: Region,
  choroplethLimits: Dictionary<ChoroplethLimits>,
  crop_ended: boolean,
  overMaxDaysColor: string | null | undefined = undefined,
  belowMinDaysColor: string | null | undefined = undefined,
  belowMinDays: number | undefined = 0
): string => {
  let backgroundColor = 'transparent';
  if (area.current_info && crop_ended) {
    backgroundColor = '#C1C5C8';
  } else if (area.current_info?.emergence_day) {
    const maxValueDae = choroplethLimits.days_after_emergence ? choroplethLimits.days_after_emergence.max : 100;

    const colors = [...MAP_DAE_COLORS_5, getStringOrDefault(overMaxDaysColor, '#000')];
    backgroundColor = getColorGeneralWithMaxValue(area.current_info?.emergence_day, colors, maxValueDae, belowMinDays, belowMinDaysColor);
  }

  return backgroundColor;
};

export const getDaysWithoutSprayColor = (
  area: Region,
  choroplethLimits: Dictionary<ChoroplethLimits>,
  crop_ended: boolean,
  overMaxDaysColor: string | null | undefined = undefined
): string => {
  let backgroundColor = 'transparent';
  const lastSpray = area.current_info?.last_spray;
  if (area.current_info && crop_ended) {
    backgroundColor = '#C1C5C8';
  } else if (area.current_info && lastSpray) {
    const lastSprayDate = lastSpray.start_date || lastSpray.end_date;
    const maxValueSpray = choroplethLimits.days_without_spray ? choroplethLimits.days_without_spray.max : 100;

    const colors = [...MAP_SPRAY_COLORS_5, getStringOrDefault(overMaxDaysColor, '#000')];
    backgroundColor = getColorGeneralWithMaxValue(lastSprayDate, colors, maxValueSpray);
  }

  return backgroundColor;
};

export const getDaysWithoutMonitoringColor = (
  area: Region,
  choroplethLimits: Dictionary<ChoroplethLimits>,
  crop_ended: boolean,
  overMaxDaysColor: string | null | undefined = undefined
): string => {
  let backgroundColor = 'transparent';
  const lastMonitoring = area.current_info?.last_monitoring;
  if (area.current_info && crop_ended) {
    backgroundColor = '#C1C5C8';
  } else if (area.current_info && lastMonitoring) {
    const maxValueSpray = choroplethLimits.days_without_monitoring ? choroplethLimits.days_without_monitoring.max : 100;

    const colors = [...MAP_SEVERITY_COLORS_5, getStringOrDefault(overMaxDaysColor, '#000')];
    backgroundColor = getColorGeneralWithMaxValue(lastMonitoring.date, colors, maxValueSpray);
  }

  return backgroundColor;
};

const resolveSeasonFlags = (
  area: RegionWithParents,
  areaSeasons?: Dictionary<CurrentSeasonArea[]>,
  selectedSeasons?: string[]
): { crop_ended: boolean; crop_future: boolean } => {
  if (area.current_info?.crop_future) return { crop_ended: false, crop_future: true };
  if (!areaSeasons) return { crop_ended: false, crop_future: false };
  return {
    crop_ended: !areaSeasons[area.id]?.some(as => selectedSeasons?.includes(as.seasonId)),
    crop_future: false
  };
};

export const getRegionFeatureCollection = (
  region: Region,
  areaSeasons?: Dictionary<CurrentSeasonArea[]>,
  selectedSeasons?: string[],
  mapLayersColorDictionary: ColorDictionary = {},
  choroplethLimits?: Dictionary<ChoroplethLimits>,
  storageMaxDays?: StorageMaxDaysLimit,
  optionalMultilayer?: OptionsMultilayer
): GeoJSON.FeatureCollection<GeoJSON.Polygon, GeometryProperties> => {
  const { showMapDAE, showMapSeedsLayer, showMapPhenologicalStageLayer } = optionalMultilayer ?? {};

  const areas = uniqBy(getRegionAreasWithParents(region), 'id');

  const isToShowDAE = choroplethLimits && showMapDAE;
  const isToShowSeed = choroplethLimits && showMapSeedsLayer;
  const isToShowPhenologicalStage = choroplethLimits && showMapPhenologicalStageLayer;

  const features = areas.map(area => {
    const { crop_ended, crop_future } = resolveSeasonFlags(area, areaSeasons, selectedSeasons);
    const geometry = area.geometry as GeoJSON.Feature<GeoJSON.Polygon, GeometryProperties>;
    const seed = area.current_info?.seeds?.[0];
    const phenologicalStage = area.current_info?.phenological_stage;
    const dae = isToShowDAE ? getDAEByEmergenceDay(area.current_info?.emergence_day) : undefined;
    const madeCalculations = area.current_info && choroplethLimits;

    const normalizedPhenologicalStage = phenologicalStage || undefined;

    const properties: GeometryProperties = {
      ...geometry.properties,
      id: area.id,
      name: area.name,
      parent_id: area.parent_id,
      total_area: area.total_area,
      type: area.type,
      parents: area.parents,
      formattedTotalArea: formatArea(area.total_area),
      ...(madeCalculations && {
        current_info: {
          ...area.current_info,
          crop_ended,
          crop_future
        } as CurrentInfo
      }),
      is_season_active: isActive(area.seasons[0]),
      isInReentryPeriod: isInReentryPeriod(area.current_info, crop_ended),
      severityLevelColor: area.current_info && getChoroplethSeverityColor(area, crop_ended),
      hasSprayAfterMonitoring: validateSprayDateTime(area.current_info, crop_ended),
      highlight: false,
      seed,
      ...(isToShowPhenologicalStage && {
        phenologicalStageColor: crop_ended
          ? CROP_ENDED_PHENOLOGY_COLOR
          : mapLayersColorDictionary?.phenologicalStageColors?.[phenologicalStage ?? '']?.color,
        phenologicalStage: crop_ended ? undefined : normalizedPhenologicalStage
      }),
      ...(isToShowSeed && {
        seedColor: crop_ended ? CROP_ENDED_PHENOLOGY_COLOR : mapLayersColorDictionary?.seedsColors?.[seed ?? '']?.color
      }),

      ...(isToShowDAE && {
        daeColor:
          madeCalculations &&
          getDaysAfterEmergenceColor(
            area,
            choroplethLimits,
            crop_ended,
            storageMaxDays?.daysAfterEmergenceMaxColor,
            storageMaxDays?.daysAfterEmergenceMinColor,
            storageMaxDays?.daysAfterEmergenceMin ?? 0
          ),
        dae
      }),
      ...(choroplethLimits && {
        daysWithoutSprayColor:
          madeCalculations && getDaysWithoutSprayColor(area, choroplethLimits, crop_ended, storageMaxDays?.daysWithoutSprayMaxColor),
        daysWithoutMonitoringColor:
          madeCalculations &&
          getDaysWithoutMonitoringColor(area, choroplethLimits, crop_ended, storageMaxDays?.daysWithoutMonitoringMaxColor)
      })
    };
    return { ...geometry, id: hashNumber(area.id), properties };
  });
  return {
    type: 'FeatureCollection',
    features
  };
};

export const buildGeometries = (
  rootRegion: Region,
  areaSeasons: CurrentSeasonArea[],
  selectedSeasons: string[],
  mapLayersColorDictionary: ColorDictionary = {},
  choroplethLimits?: Dictionary<ChoroplethLimits>,
  storageMaxDays?: StorageMaxDaysLimit,
  optionalMultilayer?: OptionsMultilayer
): Region => {
  const dictSeasonAreas = groupBy(areaSeasons, 'areaId');

  const allRegions = getRegionsChild(rootRegion).map(child => {
    return {
      ...child,
      children: mapAreaChildren(child.children as Region[], dictSeasonAreas, selectedSeasons),
      geometry: getRegionFeatureCollection(
        child,
        dictSeasonAreas,
        selectedSeasons,
        mapLayersColorDictionary,
        choroplethLimits,
        storageMaxDays,
        optionalMultilayer
      )
    };
  });
  const areas = rootRegion.children?.filter(child => typeof child === 'object' && child?.type === RegionType.AREA) as Region[];

  const areaChildren = mapAreaChildren(areas, dictSeasonAreas, selectedSeasons);
  const result = {
    ...rootRegion,
    children: [...allRegions, ...areaChildren] as Region[],
    geometry: getRegionFeatureCollection(
      rootRegion,
      dictSeasonAreas,
      selectedSeasons,
      mapLayersColorDictionary,
      choroplethLimits,
      storageMaxDays,
      optionalMultilayer
    )
  };
  return result;
};

export interface RecalculateRegionsArgs {
  currentInfoRegions: Record<string, CurrentInfo>;
  regions: Dictionary<Region>;
  rootRegionId: string | undefined;
  currentAreaSeasons: CurrentSeasonArea[];
  selectedSeasonsIds: string[];
  propertyData: PropertyInfoAddress | null;
  systemFlags: Dictionary<string | boolean> | null;
  transformRootRegion?: (rootRegion: Region) => Region;
}

export const recalculateRegions = ({
  currentInfoRegions,
  regions,
  rootRegionId,
  currentAreaSeasons,
  selectedSeasonsIds,
  propertyData,
  systemFlags,
  transformRootRegion
}: RecalculateRegionsArgs) => {
  const newRegions = Object.keys(currentInfoRegions).reduce((acc, currentRegionId) => {
    if (acc[currentRegionId]) acc[currentRegionId].current_info = currentInfoRegions[currentRegionId];
    return acc;
  }, regions);
  const rootRegion = rootRegionWithChildren(newRegions, rootRegionId ?? '');

  const storageMaxDays = getStorageDaysLimit();

  const choroplethLimits = getLimitsChoropleth(
    rootRegion.children as Region[],
    propertyData,
    currentAreaSeasons,
    selectedSeasonsIds,
    storageMaxDays
  );
  const seedsColors = systemFlags?.P40_29700_show_statistic_legend
    ? getSortedSeedsChoropleth(currentInfoRegions)
    : getSeedsChoropleth(currentInfoRegions);
  const phenologicalStageColors = systemFlags?.P40_29700_show_statistic_legend
    ? getSortedPhenologicalStageColor(currentInfoRegions, {})
    : getPhenologicalStageColor(currentInfoRegions);
  const mapLayersColorDictionary = {
    seedsColors,
    phenologicalStageColors
  };
  const recursiveRegionWithGeometry = buildGeometries(
    rootRegion,
    currentAreaSeasons,
    selectedSeasonsIds,
    mapLayersColorDictionary,
    choroplethLimits,
    storageMaxDays,
    { showMapDAE: true, showMapSeedsLayer: true, showMapPhenologicalStageLayer: true }
  );

  const transformedRegion = transformRootRegion ? transformRootRegion(recursiveRegionWithGeometry) : recursiveRegionWithGeometry;

  const normalizedRegions = normalizeRegion(transformedRegion);

  return { normalizedRegions, storageMaxDays, mapLayersColorDictionary };
};

const isAreaRegion = (region: Region): boolean => region.type === RegionType.AREA;

export const getFieldsRecursive = (rootRegion: Region): Region[] => {
  const allRegions = flattenArrayByKey((rootRegion?.children as Region[]) ?? [], 'children');
  return allRegions.filter(isAreaRegion);
};
